import React, { useMemo } from 'react';
import { formatDate } from '../../utils/date';
import './File.css';
import DodoHeader from './DodoHeader';
import LegalConditions from './legalConditions';
import { formatDni } from '../../utils/formatters';

const sumByCurrency = (items, field) => {
  return (items || []).reduce((acc, item) => {
    const currency = item.currency || '$';
    const value = Number(item?.[field] || 0);
    acc[currency] = (acc[currency] || 0) + value;
    return acc;
  }, {});
};

const money = (currency, value) =>
  `${currency} ${Number(value || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;

const normalizePaymentType = (type) => {
  const t = String(type || '').trim().toLowerCase();
  if (!t) return '-';
  if (t === 'tranferencia' || t === 'transferencia') return 'Transferencia';
  if (t === 'efectivo') return 'Efectivo';
  if (t === 'credito' || t === 'crédito') return 'Crédito';
  return type;
};

const PrintFile = ({ file }) => {
  const passengers = useMemo(() => {
    const fromApi = file?.passengers || file?.passengerList || file?.passenger_list || [];
    if (Array.isArray(fromApi) && fromApi.length > 0) return fromApi;

    return [
      {
        surname: file?.client_surname,
        name: file?.client_name,
        dni: file?.client_dni || file?.clientDni,
        birthday: file?.client_birthday || file?.clientBirthday
      }
    ].filter((p) => p?.surname || p?.name);
  }, [file]);

  const services = useMemo(() => {
    const list = Array.isArray(file?.services) ? file.services : [];
    return [...list].sort((a, b) => {
      const aId = Number(a?.id) || 0;
      const bId = Number(b?.id) || 0;
      return aId - bId;
    });
  }, [file]);

  const serviceTotals = useMemo(() => sumByCurrency(file?.services, 'price'), [file]);
  const payments = useMemo(() => {
    const list = Array.isArray(file?.payments) ? file.payments : [];
    return [...list].sort((a, b) => {
      const ad = a?.date || '';
      const bd = b?.date || '';
      return String(ad).localeCompare(String(bd));
    });
  }, [file]);
  const paymentTotals = useMemo(() => sumByCurrency(file?.payments, 'value'), [file]);
  const saldo = useMemo(() => {
    const result = { ...serviceTotals };
    Object.entries(paymentTotals).forEach(([currency, val]) => {
      result[currency] = (result[currency] || 0) - val;
    });
    return result;
  }, [serviceTotals, paymentTotals]);

  const fileDateIn = useMemo(() => {
    if (!file) return null;
    return file?.date_in ? file.date_in.slice(0, 10) : (file?.dateIn || null);
  }, [file]);

  const fileDateOut = useMemo(() => {
    if (!file) return null;
    return file?.date_out ? file.date_out.slice(0, 10) : (file?.dateOut || null);
  }, [file]);

  return (
    <div className="print-a4">
      <div className="print-header">
        <DodoHeader />

        <div className="print-header-right">
          <div className="print-doc-title">FICHA DE RESERVA</div>
          <div>N°: {file?.id}</div>
          <div>FECHA DE ALTA: {formatDate(file?.creation_date || file?.creationDate)}</div>
          <div>FECHA DE SALIDA: {formatDate(fileDateIn) || '-'}</div>
        </div>
      </div>

      <div className="print-section">
        <div className="print-row">
          <div>
            <span className="print-meta-label">PASAJERO TITULAR:</span>{' '}
            {file?.client_surname} / {file?.client_name}
          </div>
          <div>
            <span className="print-meta-label">VENDEDOR:</span> {file?.seller_name || file?.seller || 'COTI'}
          </div>
        </div>
      </div>

      <div className="print-section">
        <div className="print-section-title">LISTADO DE PASAJEROS</div>
        <table className="print-table">
          <thead>
            <tr>
              <th>APELLIDO/ NOMBRE</th>
              <th>DNI</th>
              <th>FECHA DE NACIMIENTO</th>
            </tr>
          </thead>
          <tbody>
            {passengers.length === 0 ? (
              <tr>
                <td colSpan={3} style={{ textAlign: 'center' }}>
                  -
                </td>
              </tr>
            ) : (
              passengers.map((p, idx) => (
                <tr key={p.id ?? idx}>
                  <td>
                    {(p?.surname || p?.last_name || '').toString().toUpperCase()}
                    {(p?.surname || p?.last_name) && '/ '}
                    {(p?.name || p?.first_name || '').toString().toUpperCase()}
                  </td>
                  <td>{formatDni(p?.dni || p?.document) || '-'}</td>
                  <td>{formatDate(p?.birthday || p?.birthdate)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="print-section">
        <div className="print-section-title">SERVICIOS CONTRATADOS</div>
        <div className="print-tables-row">
          <table className="print-table print-table--compact print-table--dates">
            <tbody>
              <tr>
                <th scope="row">FECHA DE SALIDA</th>
                <td>{formatDate(fileDateIn) || '-'}</td>
              </tr>
              <tr>
                <th scope="row">FECHA DE REGRESO</th>
                <td>{formatDate(fileDateOut) || '-'}</td>
              </tr>
            </tbody>
          </table>

          <table className="print-table print-table--details">
            <thead>
              <tr>
                <th>DETALLE DEL SERVICIO</th>
              </tr>
            </thead>
            <tbody>
              {services.length === 0 ? (
                <tr>
                  <td colSpan={1} style={{ textAlign: 'center' }}>
                    -
                  </td>
                </tr>
              ) : (
                services.map((s) => (
                  <tr key={s.id}>
                    <td>{s.description || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="print-totals">
          <div className="print-total-block">
            <span className="print-total-label">TOTAL</span>
            <div className="print-total-values">
              {Object.keys(serviceTotals).length === 0 ? (
                <div className="print-total-item">-</div>
              ) : (
                Object.entries(serviceTotals).map(([currency, val]) => (
                  <div key={currency} className="print-total-item">
                    {money(currency, val)}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="print-section">
        <div className="print-section-title">PAGOS</div>
        <table className="print-table print-table--payments">
          <thead>
            <tr>
              <th style={{ width: '16%' }}>FECHA</th>
              <th style={{ width: '20%' }}>TIPO</th>
              <th>DETALLE</th>
              <th style={{ width: '20%', textAlign: 'right' }}>MONTO</th>
            </tr>
          </thead>
          <tbody>
            {payments.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center' }}>
                  -
                </td>
              </tr>
            ) : (
              payments.map((p, idx) => (
                <tr key={p?.id ?? idx}>
                  <td>{formatDate(p?.date) || '-'}</td>
                  <td>{normalizePaymentType(p?.type) || '-'}</td>
                  <td>{(p?.detail || '-').toString()}</td>
                  <td style={{ textAlign: 'right' }}>{money(p?.currency || '$', p?.value)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="print-totals">
          <div className="print-total-block">
            <span className="print-total-label">TOTAL</span>
            <div className="print-total-values">
              {Object.keys(paymentTotals).length === 0 ? (
                <div className="print-total-item">-</div>
              ) : (
                Object.entries(paymentTotals).map(([currency, val]) => (
                  <div key={currency} className="print-total-item">
                    {money(currency, val)}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="print-saldo">
          <div className="print-saldo-label">SALDO RESTANTE</div>
          <div className="print-saldo-box">
            {Object.keys(saldo).length === 0 ? (
              <div className="print-saldo-item">-</div>
            ) : (
              Object.entries(saldo).map(([currency, val], idx) => (
                <div key={currency} className="print-saldo-item" style={idx === 0 ? { borderTop: 'none' } : {}}>
                  {money(currency, val)}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <LegalConditions />
    </div>
  );
};

export default PrintFile;
